import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";

import {
  ACP_SUPPORT_EMAIL,
  ACP_SUPPORT_EMAIL_HREF,
  SUPPORT_EMAIL,
  SUPPORT_EMAIL_HREF,
  SUPPORT_NUMBER,
  SUPPORT_NUMBER_HREF,
} from "@/constants/aptihealth";
import { AcpStatusBadge } from "@/features/acp/components/AcpStatusBadge";
import { Button2 } from "@/ui2/atoms/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { ConsentedPolicyEnum } from "@/core/constants";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getConsentPayload } from "@/lib/helpers/getConsentPayload";
import { getCurrentPolicyVersion } from "@/lib/helpers/getCurrentPolicyVersion";
import { getPatientProfile } from "@/redux/actions/auth";
import { Link2 } from "@/ui2/atoms/Link";
import { selectIsGlobalLoading } from "@/redux/selectors/loaderSelectors";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { showToast } from "@/providers/ToastProvider";
import { useACPStatus } from "@/features/acp/hooks/useACPStatus";
import { useAppendConsent } from "@/features/consent/api/mutations/useAppendConsent";
import { usePolicyMetadata } from "@/features/consent/api/queries/usePolicyMetadata";

import { Footer } from "./components/Footer";
import { SupportLink } from "./components/SupportLink";

export function Consents() {
  const dispatch = useDispatch();
  const profile = useSelector(selectProfile);
  const isLoadingGlobal = useSelector(selectIsGlobalLoading);
  const { isPending, isSuccess, mutateAsync: appendConsent } = useAppendConsent();
  const { data: policyMetadata } = usePolicyMetadata();
  const consentStatus = useACPStatus();

  const isOptedIn = consentStatus === "OPTED_IN";
  const isOptedOut = consentStatus === "OPTED_OUT";
  const isNotResponded = consentStatus === "NOT_RESPONDED";

  const patientId = profile?.username;
  const policyVersion = getCurrentPolicyVersion({
    policy: "ADVANCED_CARE_PROGRAM",
    policyMetadata,
  });

  const handleConsentSubmit = async () => {
    if (!policyMetadata || !patientId) return;

    try {
      const payload = getConsentPayload({
        consentResponse: true,
        consentType: ConsentedPolicyEnum["ADVANCED_CARE_PROGRAM"],
        patientId,
        policyMetadata,
        policyVersion,
      });

      await appendConsent(payload);
      /** Refetch profile to redraw the updated view
       * This is a temporary solution until we move profile data to tanstack query
       */
      dispatch(getPatientProfile(true));
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Consent submission error" }), {
        tags: {
          component: "Consents",
          consentType: "ADVANCED_CARE_PROGRAM",
          errorType: "consent_submission",
          patientId: patientId || "unknown",
        },
      });

      showToast([
        {
          heading: "Something went wrong. Please reload the page and try again.",
          variant: "error",
        },
      ]);
    }
  };

  // trigger toast after both mutation and loader conclude
  useEffect(() => {
    if (!isPending && !isLoadingGlobal && isSuccess) {
      showToast([{ heading: "Advanced Care Program consent confirmed.", variant: "success" }]);
    }
  }, [isPending, isLoadingGlobal, isSuccess]);

  return (
    <DashboardCard2
      Actions={(isOptedIn || isOptedOut) && <AcpStatusBadge status={consentStatus} />}
      classNameChildren="tw:flex tw:flex-col tw:gap-4 tw:items-start"
      title="Advanced Care Program Consent"
    >
      <p className="body-md tw:md:max-w-3/5" {...getAutomationDataAttr("consents-acp-description")}>
        Your insurance may cover additional services to support your care journey. By providing
        consent, you agree to participate in the{" "}
        {/* Link to www because using Policy component and /advanced-care route broke routing */}
        <Link2 href="https://www.aptihealth.com/advanced-care-program" isExternal>
          Advanced Care Program
        </Link2>
        .<br />
        Your care team will determine if these services are right for you.
      </p>

      {(isOptedOut || isNotResponded) && (
        <Button2
          className="tw:mt-2"
          isLoading={isPending || isSuccess}
          onPress={handleConsentSubmit}
          size="lg"
          {...getAutomationDataAttr("consents-agree-to-acp-button")}
        >
          Agree to Advanced Care Program
        </Button2>
      )}

      {(isOptedIn || isNotResponded) && <hr className="tw:m-0! tw:w-full" />}

      {isOptedIn && (
        <Footer {...getAutomationDataAttr("consents-acp-opted-in-support-contact")}>
          If you wish to opt-out of the program, please contact us{" "}
          <span className="tw:whitespace-nowrap">
            at
            <SupportLink href={SUPPORT_EMAIL_HREF}>{SUPPORT_EMAIL}</SupportLink>
          </span>
          or call us{" "}
          <span className="tw:whitespace-nowrap">
            at
            <SupportLink href={SUPPORT_NUMBER_HREF}>{SUPPORT_NUMBER}</SupportLink>
          </span>
        </Footer>
      )}

      {isNotResponded && (
        <Footer {...getAutomationDataAttr("consents-acp-no-response-description")}>
          If you're not interested in the Advanced Care Program, please contact us{" "}
          <span className="tw:whitespace-nowrap">
            at
            <SupportLink href={ACP_SUPPORT_EMAIL_HREF}>{ACP_SUPPORT_EMAIL}</SupportLink>
          </span>
          and we'll update your preferences accordingly.
        </Footer>
      )}
    </DashboardCard2>
  );
}
